import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Download, FileSpreadsheet, ClipboardList, Lock, ArrowRight } from "lucide-react";

export default function Reports() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [evalSummary, setEvalSummary] = useState(null);

  useEffect(() => {
    api.get("/reports/summary").then((r) => setRows(r.data));
    api.get("/evaluations/summary").then((r) => setEvalSummary(r.data)).catch(() => {});
    if (isAdmin) api.get("/invoices").then((r) => setInvoices(r.data));
  }, []);

  if (!rows) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="reports-loading">Loading reports…</div>;

  return (
    <div className="max-w-6xl space-y-8" data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-4 fade-up">
        <div>
          <p className="eyebrow">Reports</p>
          <h1 className="section-title text-3xl mt-1">Reports & Service Totals</h1>
          <p className="mt-3 text-sm text-[#57534E] max-w-2xl">
            Export-ready layout. Report delivery, exports, surveys, and invoicing activate in a later phase.
          </p>
        </div>
        <button className="btn-outline opacity-60 cursor-not-allowed" disabled title="Export activates in a later phase"
          data-testid="export-report-button"><Download size={15} /> Export (coming later)</button>
      </div>

      <div className="card overflow-hidden fade-up-1" data-testid="service-totals-card">
        <div className="px-6 pt-5 pb-3 flex items-center gap-2">
          <FileSpreadsheet size={17} className="text-terracotta" />
          <h2 className="font-serif font-bold text-lg text-navy">Service-day totals by engagement</h2>
          <span className="badge-neutral" data-testid="reports-placeholder-badge">Placeholder</span>
        </div>
        <table className="w-full">
          <thead className="bg-sand/60"><tr>
            <th className="table-th">Engagement</th><th className="table-th">Organization</th>
            <th className="table-th">Planned</th><th className="table-th">Delivered</th>
            <th className="table-th">By service type</th><th className="table-th">Approved logs</th>
            <th className="table-th">Report status</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.engagement_id} className="hover:bg-canvas" data-testid={`report-row-${r.engagement_id}`}>
                <td className="table-td font-bold max-w-[220px]">{r.title}</td>
                <td className="table-td">{r.organization_name}</td>
                <td className="table-td">{r.planned_days}</td>
                <td className="table-td font-black text-navy">{r.delivered_days}</td>
                <td className="table-td text-xs text-[#57534E]">
                  {Object.entries(r.by_service_type).map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}</td>
                <td className="table-td">{r.approved_logs}</td>
                <td className="table-td"><span className="badge-neutral">{r.note}</span></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="table-td text-center text-[#78716C] py-8">No engagements yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 fade-up-2">
        <div className="card p-6" data-testid="surveys-placeholder-card">
          <div className="flex items-center gap-2 mb-3">
            <ClipboardList size={17} className="text-terracotta" />
            <h2 className="font-serif font-bold text-lg text-navy">Teacher evaluations</h2>
            <span className="badge-approved">Built-in form active</span>
          </div>
          <p className="text-sm text-[#57534E]">
            Teacher follow-up drafts automatically include a secure link to the built-in CKingEducation
            Teacher Feedback form. <b>{evalSummary ? evalSummary.total_responses : 0} response{(evalSummary?.total_responses ?? 0) === 1 ? "" : "s"}</b> collected
            — a sent link never counts as a completed evaluation.
          </p>
          <Link to="/evaluations" className="btn-outline mt-4" data-testid="view-evaluations-link">
            View results <ArrowRight size={14} /></Link>
        </div>

        {isAdmin && (
          <div className="card p-6" data-testid="invoice-placeholder-card">
            <div className="flex items-center gap-2 mb-3">
              <FileSpreadsheet size={17} className="text-terracotta" />
              <h2 className="font-serif font-bold text-lg text-navy">Invoice-ready service entries</h2>
              <span className="badge-nosend"><Lock size={11} /> Invoicing disabled</span>
            </div>
            <p className="text-sm text-[#57534E] mb-4">
              Approved logs create an entry here. Rates and hours are set by an Admin only when invoicing is
              activated — the agent never creates billing amounts.
            </p>
            <table className="w-full">
              <thead><tr><th className="table-th">Period</th><th className="table-th">Consultant</th>
                <th className="table-th">Group</th><th className="table-th">Days</th><th className="table-th">Status</th></tr></thead>
              <tbody>
                {invoices.map((e) => (
                  <tr key={e.id} data-testid={`invoice-row-${e.id}`}>
                    <td className="table-td font-bold">{e.period}</td>
                    <td className="table-td">{e.consultant_name}</td>
                    <td className="table-td">{e.group_label}</td>
                    <td className="table-td">{e.billable_days}</td>
                    <td className="table-td"><span className="badge-neutral">{e.status}</span></td>
                  </tr>
                ))}
                {invoices.length === 0 && <tr><td colSpan={5} className="table-td text-center text-[#78716C] py-6" data-testid="invoice-empty">Invoice drafts appear after records are approved and drafts are prepared on the Invoices page.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
